import React, { useMemo, useState } from "react";
import {
  DndContext, DragOverlay, PointerSensor, KeyboardSensor, useSensor, useSensors,
  useDraggable, useDroppable, closestCorners,
} from "@dnd-kit/core";
import { MessageSquare, UserRound, GripVertical } from "lucide-react";
import { PriorityBadge, CategoryBadge, AWAITING_CONFIRMATION, STATUS_VAR } from "@/components/FeedbackBadge";
import { statusLabel } from "@/lib/labels";
import { buildBoard } from "@/components/boardModel";

// A kanban over the tracker. Columns are statuses; dragging a card to another
// column is the same status PATCH the detail panel makes. Lanes (optional)
// group rows by project, app, assignee or priority -- the Monday shape with
// no second data model underneath.

const dropId = (laneKey, status) => `${laneKey}|${status}`;
const parseDrop = (id) => { const i = String(id).lastIndexOf("|"); return { laneKey: String(id).slice(0, i), status: String(id).slice(i + 1) }; };

function Card({ t, active, nameOf, onOpen, dragging, overlay }) {
  const awaiting = t.status === AWAITING_CONFIRMATION;
  const assignee = nameOf(t.assignee_id);
  return (
    <div
      data-testid={overlay ? undefined : `board-card-${t.id}`}
      className={`group/card relative border bg-surface p-2.5 text-left select-none ${
        active ? "border-brand" : "border-line/10"
      } ${dragging ? "opacity-30" : overlay ? "shadow-2xl rotate-1" : "hover:border-line/30"}`}
      style={awaiting ? { borderColor: "rgb(var(--agent-needs-review))" } : undefined}
    >
      <button type="button" onClick={onOpen} className="w-full text-left" aria-label={`Deschide ${t.title}`}>
        <p className="text-[13px] font-semibold leading-snug break-words pr-4">{t.title}</p>
        <div className="mt-1.5 flex items-center gap-1 flex-wrap">
          <PriorityBadge value={t.priority} />
          <CategoryBadge value={t.category} />
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-2 text-[10px] text-fg/50">
          <span className="mono truncate">{t.app}</span>
          <span className="flex items-center gap-2 shrink-0">
            {t.replies?.length > 0 && (
              <span className="flex items-center gap-0.5"><MessageSquare className="w-3 h-3" />{t.replies.length}</span>
            )}
            {assignee
              ? <span className="flex items-center gap-0.5 max-w-[110px]"><UserRound className="w-3 h-3 shrink-0" /><span className="truncate">{assignee}</span></span>
              : <span className="text-fg/30">neatribuit</span>}
          </span>
        </div>
      </button>
      <GripVertical className="absolute top-2 right-1 w-3.5 h-3.5 text-fg/20 group-hover/card:text-fg/50" aria-hidden />
    </div>
  );
}

function DraggableCard(props) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: props.t.id, data: { status: props.t.status },
  });
  return (
    <div ref={setNodeRef} {...attributes} {...listeners} className="touch-none">
      <Card {...props} dragging={isDragging} />
    </div>
  );
}

function Column({ laneKey, status, tickets, showHeader, nameOf, detailId, onOpen }) {
  const { setNodeRef, isOver } = useDroppable({ id: dropId(laneKey, status) });
  const varName = STATUS_VAR[status];
  return (
    <div
      ref={setNodeRef}
      data-testid={`board-column-${status}${laneKey === "all" ? "" : `-${laneKey}`}`}
      className={`flex flex-col min-w-[240px] w-[240px] shrink-0 border transition-colors ${
        isOver ? "border-brand/60 bg-brand/5" : "border-line/10 bg-surface-2/40"
      }`}
    >
      {showHeader && (
        <div className="px-2.5 py-2 border-b border-line/10 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: varName ? `rgb(var(${varName}))` : "currentColor" }} />
          <span className="text-xs font-semibold">{statusLabel(status)}</span>
          <span className="ml-auto text-[10px] mono text-fg/45">{tickets.length}</span>
        </div>
      )}
      <div className="p-2 space-y-2 min-h-[56px] flex-1">
        {tickets.map((t) => (
          <DraggableCard key={t.id} t={t} active={detailId === t.id} nameOf={nameOf} onOpen={() => onOpen(t.id)} />
        ))}
        {tickets.length === 0 && <div className="h-10 border border-dashed border-line/10" aria-hidden />}
      </div>
    </div>
  );
}

export default function TicketBoard({ tickets, groupBy, showDone, nameOf, projectName, detailId, onOpen, onMove }) {
  const { columns, lanes } = useMemo(
    () => buildBoard(tickets, { groupBy, showDone, nameOf, projectName }),
    [tickets, groupBy, showDone, nameOf, projectName]);
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(
    // A click must still open the card: only a real drag (6px) starts one.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );
  const byId = useMemo(() => new Map(tickets.map((t) => [t.id, t])), [tickets]);
  const active = activeId ? byId.get(activeId) : null;

  const onDragEnd = ({ active: a, over }) => {
    setActiveId(null);
    if (!over) return;
    const t = byId.get(a.id);
    const { status } = parseDrop(over.id);
    if (!t || !status || status === t.status) return;
    onMove(t, status);
  };

  if (lanes.length === 0) {
    return <div className="p-10 text-center text-sm text-fg/40" data-testid="board-empty">Nimic de afișat cu filtrele curente.</div>;
  }
  const multi = groupBy !== "none";
  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners}
                onDragStart={({ active: a }) => setActiveId(a.id)} onDragEnd={onDragEnd} onDragCancel={() => setActiveId(null)}>
      <div className="h-full overflow-auto p-4" data-testid="ticket-board">
        <div className="inline-block min-w-full align-top">
          {lanes.map((lane, i) => (
            <div key={lane.key} className={multi ? "mb-4" : ""}>
              {multi && (
                <div className="flex items-center gap-2 mb-2 sticky left-0" data-testid={`board-lane-${lane.key}`}>
                  <span className="text-xs font-bold tracking-tight">{lane.label}</span>
                  <span className="text-[10px] mono text-fg/45">{lane.count}</span>
                </div>
              )}
              <div className="flex gap-3 items-start">
                {columns.map((status) => (
                  <Column key={status} laneKey={lane.key} status={status} tickets={lane.cells[status]}
                          showHeader={!multi || i === 0} nameOf={nameOf} detailId={detailId} onOpen={onOpen} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <DragOverlay dropAnimation={null}>
        {active ? <div className="w-[224px]"><Card t={active} nameOf={nameOf} overlay onOpen={() => {}} /></div> : null}
      </DragOverlay>
    </DndContext>
  );
}
